import { Result, Ok, Err } from 'ts-res';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { INode, Node } from './node';
import {
  EvaluationDataType,
  GroupedSegmentsType,
  LeafNodeType,
  NestedConditionGroupType,
  NonLeafNodeType,
  Operator,
  SegmentType,
  TargetingType,
} from './types';
import { KameleoonLogger } from 'src/logging';

interface ITree {
  evaluate: (data: EvaluationDataType) => Result<boolean, KameleoonError>;
  hasTargetingType: (targetingType: TargetingType) => boolean;
}

export class Tree implements ITree {
  private tree: Node;
  private error: KameleoonError | null = null;
  private targetingTypes: Set<TargetingType> = new Set();

  constructor(segment: SegmentType) {
    KameleoonLogger.debug`CALL: new Tree(segment: ${segment})`;
    const { nestedConditions, topLevelOperators } =
      this.flattenSegment(segment);

    // --- Note ---
    // - Targeting tree is built bottom-up, it's the only way to both get the consistent logic
    // (grouping nested conditions) and have human readable tree representation.
    // - Here is an example of how tree is built (we use strict complete binary tree)
    // Let's say we have such a condition `value < 10 || value > 20 && value < 30`
    // Following is pseudo code for building tree:
    //
    // `const operators = [Operator.Or, Operator.And]`
    // `const conditions = [
    //     {type: Condition.Less, value: 10},
    //     {type: Condition.Greater, value: 20},
    //     {type: Condition.Less, value: 30},
    // ]`
    //
    // After the first iteration we have node like this:
    //    AND
    //   /   \
    //  <30  >20
    //
    // After the second iteration tree looks as follows:
    //         OR
    //        /  \
    //    AND    <10
    //   /   \
    //  <30  >20
    //
    // As you can see Leaf Nodes contain conditions and Non-Leaf Nodes contain logical operators
    const nestedNodes = nestedConditions.map(({ conditions, operators }) => {
      const nodes = conditions.map((condition) => {
        // --- Note ---
        // We keep condition types to optimize
        // some external operations depending on what conditions are present
        // in a tree
        this.targetingTypes.add(condition.targetingType);

        return new Node(condition);
      });

      return this.buildTree(nodes, operators);
    });

    this.tree = this.buildTree(nestedNodes, topLevelOperators);
    KameleoonLogger.debug`RETURN: new Tree(segment: ${segment})`;
  }

  public evaluate(data: EvaluationDataType): Result<boolean, KameleoonError> {
    const result = this.evaluateNode(this.tree, data);

    if (this.error) {
      return Err(this.error);
    }

    return Ok(result);
  }

  public hasTargetingType(targetingType: TargetingType): boolean {
    return this.targetingTypes.has(targetingType);
  }

  private evaluateNode(node: Node, data: EvaluationDataType): boolean {
    if (this.isLeafNode(node)) {
      if (node.error) {
        // --- Note ---
        // Non-existing condition should not break targeting tree
        // instead it should be treated as `true` condition
        if (node.error.type === KameleoonException.TargetingCondition) {
          return true;
        }

        return false;
      }

      const result = node.value.evaluate(data);

      if (!result.ok) {
        this.error = result.error;

        return false;
      }

      return node.inverse ? !result.data : result.data;
    }

    if (this.isNonLeafNode(node)) {
      switch (node.value) {
        case Operator.And: {
          return (
            this.evaluateNode(node.left, data) &&
            this.evaluateNode(node.right, data)
          );
        }
        case Operator.Or: {
          return (
            this.evaluateNode(node.left, data) ||
            this.evaluateNode(node.right, data)
          );
        }
        default:
          break;
      }
    }

    return false;
  }

  private isLeafNode(node: INode): node is LeafNodeType {
    return !Boolean(node.left && node.right);
  }

  private isNonLeafNode(node: INode): node is NonLeafNodeType {
    return Boolean(
      node.left &&
        node.right &&
        (node.value === Operator.Or || node.value === Operator.And),
    );
  }

  // --- Note ---
  // This method mutates `nodes` array
  // It's done for the sake of performance
  private buildTree(nodes: Node[], operators: Operator[]): Node {
    if (nodes.length === 1) {
      return nodes[0];
    }

    for (const operator of operators) {
      const left = nodes.shift();
      const right = nodes.shift();

      const newNode = new Node(operator, left, right);
      nodes.unshift(newNode);
    }

    return nodes[0];
  }

  private flattenSegment(segment: SegmentType): GroupedSegmentsType {
    const { firstLevel: conditions, firstLevelOrOperators: operators } =
      segment.conditionsData;

    const nestedConditions: NestedConditionGroupType[] = [];

    conditions.forEach((condition) => {
      const { conditions: conditionList, orOperators } = condition;

      const group = {
        operators: this.convertOperators(orOperators),
        conditions: conditionList,
      };

      nestedConditions.push(group);
    });

    return {
      topLevelOperators: this.convertOperators(operators),
      nestedConditions,
    };
  }

  private convertOperators(operators: boolean[]): Operator[] {
    return operators.map((operator) => (operator ? Operator.Or : Operator.And));
  }
}
